/*
 * Vendored from React Bits (https://github.com/DavidHDev/react-bits), src/ts-default/Micro/HoldButton/HoldButton.tsx
 * Upstream commit e1bbb696fc53f7f91e694c529e4d68c899773b6e (2026-09-30). Licence: MIT + Commons Clause, see ../LICENSE.md.
 * Use only inside this product; do not redistribute these components as a kit or package.
 * Change: openspec/changes/add-subtle-portal-micro-interactions.
 * Local patches:
 *   1. Accessibility: the hidden hint is rendered next to the button instead of inside it, so it no longer becomes part of the accessible name; an external description can replace it (describedBy).
 *   2. Accessibility: a synthetic click without a preceding pointer or key gesture calls onTap, so assistive technology and voice control have a non-hold route.
 *   3. onRelease callback when a hold ends early, so the wrapper can explain the gesture.
 */
'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import './HoldButton.css';
const TAP_MS = 250;
const HIT_PAD = 10;
const LINEAR = (t) => t;
const EASE_OUT = (t) => 1 - Math.pow(1 - t, 3);
const HoldButton = ({ children = 'Hold to delete', doneLabel = 'Deleted', icon = null, doneIcon = null, backgroundColor = '#27272a', fillColor = '#5227FF', textColor = '#f5f5f5', fillTextColor = '#ffffff', size = 'md', radius = 14, fillDirection = 'right', holdTime = 2000, releaseTime = 200, pressScale = 0.97, wave = true, waveAmplitude = 6, glow = true, resetAfter = 1200, disabled = false, onHold, onTap, onRelease, describedBy, className = '' }) => {
    const [phase, setPhase] = useState('idle');
    const [input, setInput] = useState(null);
    const phaseRef = useRef('idle');
    const inputRef = useRef(null);
    const buttonRef = useRef(null);
    const gesture = useRef({ pointerId: null, start: 0, rect: null });
    const timers = useRef({ complete: 0, reset: 0 });
    const hintId = useId();
    const lastGestureAt = useRef(Number.NEGATIVE_INFINITY);
    const go = (next, kind = null) => {
        phaseRef.current = next;
        inputRef.current = kind;
        setPhase(next);
        setInput(kind);
    };
    const clearTimers = () => {
        clearTimeout(timers.current.complete);
        clearTimeout(timers.current.reset);
    };
    const motion = useRef({ raf: 0, p: 0, from: 0, to: 0, start: 0 });
    const drive = (to, duration, ease) => {
        const m = motion.current;
        cancelAnimationFrame(m.raf);
        m.from = m.p;
        m.to = to;
        m.start = performance.now();
        const step = (now) => {
            const t = duration > 0 ? Math.min(1, (now - m.start) / duration) : 1;
            m.p = m.from + (m.to - m.from) * ease(t);
            buttonRef.current?.style.setProperty('--hb-p', m.p.toFixed(4));
            if (t < 1) {
                m.raf = requestAnimationFrame(step);
                return;
            }
            m.raf = 0;
            if (m.to === 1)
                complete();
        };
        m.raf = requestAnimationFrame(step);
    };
    const complete = () => {
        if (phaseRef.current !== 'holding')
            return;
        if (performance.now() - gesture.current.start < holdTime - 50)
            return;
        clearTimers();
        go('done', inputRef.current);
        onHold?.();
        if (resetAfter > 0) {
            timers.current.reset = window.setTimeout(() => {
                go('idle');
                drive(0, releaseTime, EASE_OUT);
            }, resetAfter);
        }
    };
    const begin = (kind) => {
        if (disabled || phaseRef.current !== 'idle')
            return false;
        const button = buttonRef.current;
        if (!button)
            return false;
        lastGestureAt.current = performance.now();
        gesture.current.start = performance.now();
        gesture.current.rect = button.getBoundingClientRect();
        go('holding', kind);
        drive(1, holdTime, LINEAR);
        timers.current.complete = window.setTimeout(complete, holdTime + 100);
        return true;
    };
    const release = ({ drifted = false } = {}) => {
        if (phaseRef.current !== 'holding')
            return;
        clearTimers();
        const held = performance.now() - gesture.current.start;
        lastGestureAt.current = performance.now();
        go('idle');
        drive(0, releaseTime, EASE_OUT);
        onRelease?.({ heldMs: held, drifted });
        if (!drifted && held < TAP_MS)
            onTap?.();
    };
    const releaseRef = useRef(release);
    releaseRef.current = release;
    const handlePointerDown = (e) => {
        if (e.button !== 0 || !e.isPrimary || gesture.current.pointerId !== null)
            return;
        if (!begin('pointer'))
            return;
        gesture.current.pointerId = e.pointerId;
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        catch { }
    };
    const endPointer = (e, options) => {
        if (e.pointerId !== gesture.current.pointerId)
            return;
        gesture.current.pointerId = null;
        try {
            if (e.currentTarget.hasPointerCapture(e.pointerId))
                e.currentTarget.releasePointerCapture(e.pointerId);
        }
        catch { }
        release(options);
    };
    const handlePointerMove = (e) => {
        if (e.pointerId !== gesture.current.pointerId)
            return;
        const r = gesture.current.rect;
        if (!r)
            return;
        const out = e.clientX < r.left - HIT_PAD ||
            e.clientX > r.right + HIT_PAD ||
            e.clientY < r.top - HIT_PAD ||
            e.clientY > r.bottom + HIT_PAD;
        if (out)
            endPointer(e, { drifted: true });
    };
    const handlePointerLeave = (e) => {
        if (e.pointerType !== 'touch')
            endPointer(e, { drifted: true });
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            if (inputRef.current === 'key')
                release({ drifted: true });
            return;
        }
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            if (!e.repeat)
                begin('key');
        }
    };
    const handleKeyUp = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            if (inputRef.current === 'key')
                release();
        }
    };
    // Local patch: assistive technology and voice control often activate a button with a synthetic
    // click and no pointer or key gesture. Treat such a click as a tap so a non-hold route exists.
    const handleClick = () => {
        if (disabled || phaseRef.current !== 'idle')
            return;
        if (performance.now() - lastGestureAt.current < 600)
            return;
        onTap?.();
    };
    useLayoutEffect(() => {
        const button = buttonRef.current;
        if (!button)
            return undefined;
        const measure = () => {
            button.style.setProperty('--hb-w', `${button.offsetWidth}px`);
            button.style.setProperty('--hb-h', `${button.offsetHeight}px`);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(button);
        return () => ro.disconnect();
    }, []);
    useEffect(() => {
        if (phase !== 'holding')
            return undefined;
        const cancel = () => releaseRef.current({ drifted: true });
        const onVisibility = () => {
            if (document.hidden)
                cancel();
        };
        window.addEventListener('blur', cancel);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('blur', cancel);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [phase]);
    useEffect(() => {
        const t = timers.current;
        const m = motion.current;
        return () => {
            clearTimeout(t.complete);
            clearTimeout(t.reset);
            cancelAnimationFrame(m.raf);
        };
    }, []);
    const direction = fillDirection === 'up' ? 'up' : 'right';
    const labels = (_jsxs(_Fragment, { children: [_jsxs("span", { className: "hold-button__idle", "aria-hidden": phase === 'done', children: [icon ? _jsx("span", { className: "hold-button__icon", children: icon }) : null, children] }), _jsxs("span", { className: "hold-button__done", "aria-hidden": phase !== 'done', children: [doneIcon ? _jsx("span", { className: "hold-button__icon", children: doneIcon }) : null, doneLabel] })] }));
    const cssVars = {
        '--hb-radius': `${radius}px`,
        '--hb-bg': backgroundColor,
        '--hb-fill': fillColor,
        '--hb-text': textColor,
        '--hb-fill-text': fillTextColor,
        '--hb-hold': `${holdTime}ms`,
        '--hb-cycles': holdTime / 1100,
        '--hb-release': `${releaseTime}ms`,
        '--hb-press': pressScale,
        '--hb-wave': `${wave ? waveAmplitude : 0}px`
    };
    return (_jsxs(_Fragment, { children: [_jsxs("button", { ref: buttonRef, type: "button", disabled: disabled, className: `hold-button hold-button--${size}${className ? ` ${className}` : ''}`, "data-phase": phase, "data-input": input ?? undefined, "data-direction": direction, "data-glow": glow ? 'true' : undefined, "aria-describedby": describedBy ?? hintId, style: cssVars, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: e => endPointer(e), onPointerCancel: e => endPointer(e, { drifted: true }), onLostPointerCapture: e => endPointer(e, { drifted: true }), onPointerLeave: handlePointerLeave, onKeyDown: handleKeyDown, onKeyUp: handleKeyUp, onContextMenu: e => e.preventDefault(), onClick: handleClick, children: [_jsx("span", { className: "hold-button__pulse", "aria-hidden": "true" }), _jsx("span", { className: "hold-button__label", children: labels }), _jsxs("span", { className: "hold-button__clip", "aria-hidden": "true", children: [_jsx("span", { className: "hold-button__fill", children: _jsx("span", { className: "hold-button__label hold-button__label--fill", children: labels }) }), _jsx("span", { className: "hold-button__crest", "aria-hidden": "true", children: _jsx("span", { className: "hold-button__label hold-button__label--fill", children: labels }) })] })] }), describedBy ? null : (_jsxs("span", { id: hintId, className: "hold-button__sr", children: ["Press and hold for ", Math.round(holdTime / 100) / 10, " seconds to confirm"] }))] }));
};
export default HoldButton;
